import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, X, Plus, Minus, Trash2, Package } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const Cart = () => {
  const {
    cartItems,
    isCartOpen,
    setIsCartOpen,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount
  } = useCart();
  const { user } = useAuth();
  const checkoutHref = user ? '/dashboard' : '/registrieren';
  const checkoutLabel = user ? 'Zur Bestellübersicht' : 'Jetzt kostenlos bestellen';

  const cartCount = getCartCount();
  const cartTotal = getCartTotal();

  return (
    <>
      {/* Floating Cart Button - Immer sichtbar (auch wenn leer) */}
      <AnimatePresence>
        {!isCartOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={() => setIsCartOpen(true)}
            className="fixed right-3 sm:right-4 bottom-[136px] z-40 bg-secondary/80 hover:bg-secondary backdrop-blur-md text-white rounded-full w-11 h-11 shadow-md flex items-center justify-center transition-all hover:scale-105 ring-1 ring-white/30 group"
            data-testid="floating-cart-button"
            aria-label="Warenkorb öffnen"
          >
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-white animate-pulse">
                {cartCount}
              </span>
            )}
            {/* Hover tooltip */}
            <span className="absolute right-full mr-2 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              {cartCount > 0 ? `${cartCount} Artikel im Warenkorb` : 'Warenkorb (leer)'}
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Cart Slide-out Panel */}
      <AnimatePresence>
        {isCartOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsCartOpen(false)}
              className="fixed inset-0 bg-black/50 z-50"
              data-testid="cart-backdrop"
            />

            {/* Cart Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col"
              data-testid="cart-panel"
            >
              {/* Header */}
              <div className="p-4 border-b border-border flex items-center justify-between bg-gradient-to-r from-primary to-primary/80">
                <div className="flex items-center gap-3 text-white">
                  <ShoppingCart className="w-6 h-6" />
                  <h2 className="text-lg font-bold">Warenkorb</h2>
                  {cartCount > 0 && (
                    <span className="bg-white/20 px-2 py-0.5 rounded-full text-sm">
                      {cartCount} Artikel
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setIsCartOpen(false)}
                  className="text-white hover:bg-white/10 p-2 rounded-full transition-colors"
                  data-testid="cart-close-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto p-4">
                {cartItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
                      <Package className="w-10 h-10 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      Ihr Warenkorb ist leer
                    </h3>
                    <p className="text-muted-foreground text-sm max-w-xs">
                      Nutzen Sie unseren Sprach-Assistenten um Produkte hinzuzufügen! Sagen Sie z.B. "Ich brauche Handschuhe"
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {cartItems.map((item) => (
                      <motion.div
                        key={item.id}
                        layout
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -100 }}
                        className="bg-muted/50 rounded-xl p-4 border border-border/50"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h4 className="font-semibold text-foreground">{item.name}</h4>
                            {item.category && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {item.category}
                              </p>
                            )}
                            <p className="text-secondary text-xs font-semibold mt-1">
                              Pflegegrad-Leistung
                            </p>
                          </div>
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="text-red-500 hover:bg-red-50 p-1.5 rounded-lg transition-colors"
                            title="Entfernen"
                            data-testid={`remove-item-${item.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Quantity Controls */}
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="w-8 h-8 rounded-lg bg-white border border-border flex items-center justify-center hover:bg-muted transition-colors"
                              data-testid={`decrease-${item.id}`}
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="w-10 text-center font-semibold">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="w-8 h-8 rounded-lg bg-white border border-border flex items-center justify-center hover:bg-muted transition-colors"
                              data-testid={`increase-${item.id}`}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Anzahl
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer with Total & Actions */}
              {cartItems.length > 0 && (
                <div className="border-t border-border p-4 bg-muted/30">
                  {/* Info Banner */}
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4">
                    <p className="text-green-700 text-sm font-medium text-center">
                      Mit Pflegegrad: 100% kostenlos! 🎉
                    </p>
                  </div>

                  {/* Total - Anzahl statt Preis */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-lg font-semibold text-foreground">Insgesamt:</span>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-primary">
                        {cartCount} Artikel
                      </span>
                      <p className="text-xs text-secondary font-semibold">
                        Vollständig durch Pflegegrad gedeckt
                      </p>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="space-y-2">
                    <a
                      href={checkoutHref}
                      onClick={() => setIsCartOpen(false)}
                      className="block w-full bg-primary text-white text-center py-3 px-4 rounded-xl font-semibold hover:bg-primary/90 transition-colors"
                      data-testid="checkout-btn"
                    >
                      {checkoutLabel}
                    </a>
                    <button
                      onClick={clearCart}
                      className="w-full text-red-500 text-center py-2 px-4 rounded-xl font-medium hover:bg-red-50 transition-colors text-sm"
                      data-testid="clear-cart-btn"
                    >
                      Warenkorb leeren
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Cart;
